import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${({ scrolled }) => (scrolled ? '#ffffff' : 'transparent')};
  padding: ${({ scrolled }) => (scrolled ? '10px 0' : '20px 0')};
  transition: all 0.3s ease;
  box-shadow: ${({ scrolled }) => (scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none')};
  z-index: 1000;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  text-decoration: none;
  color: #000;
  
  &:hover {
    color: #555;
  }
`;

const Nav = styled.nav`
  display: flex;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  margin-left: 30px;
  text-decoration: none;
  color: ${({ active }) => (active ? '#000' : '#666')};
  position: relative;
  font-weight: ${({ active }) => (active ? '600' : '400')};

  &:after {
    content: '';
    position: absolute;
    width: ${({ active }) => (active ? '100%' : '0')};
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #000;
    transition: width 0.3s;
  }

  &:hover {
    color: #000;

    &:after {
      width: 100%;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #000;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 70%;
  max-width: 300px;
  height: 100vh;
  background-color: #fff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  display: flex;
  flex-direction: column;
  z-index: 1001;
  overflow-y: auto;
`;

const MobileNavItem = styled(Link)`
  padding: 15px 0;
  text-decoration: none;
  color: #333;
  font-size: 1.2rem;
  border-bottom: 1px solid #eee;
  transition: all 0.3s;
  display: block;
  
  &:hover {
    color: #000;
    padding-left: 5px;
  }
`;

const MobileCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #000;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
  
  &:hover {
    color: #555;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <HeaderContainer scrolled={scrolled}>
      <HeaderContent>
        <Logo to="/">
          DY
        </Logo>
        
        <Nav>
          <NavItem
            to="/"
            active={location.pathname === '/' ? 1 : 0}
          >
            Home
          </NavItem>
          <NavItem
            to="/blog"
            active={location.pathname === '/blog' ? 1 : 0}
          >
            Blog
          </NavItem>
          <NavItem
            to="/contact"
            active={location.pathname === '/contact' ? 1 : 0}
          >
            Contact
          </NavItem>
        </Nav>

        <MobileMenuButton onClick={toggleMenu} aria-label="Open menu">
          <FontAwesomeIcon icon={faBars} />
        </MobileMenuButton>

        <AnimatePresence>
          {isOpen && (
            <>
              <Overlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMenu}
              />
              <MobileMenu
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3 }}
              >
                <MobileCloseButton onClick={closeMenu} aria-label="Close menu">
                  <FontAwesomeIcon icon={faTimes} />
                </MobileCloseButton>
                <div style={{ marginTop: '60px' }}>
                  <MobileNavItem to="/" onClick={closeMenu}>
                    Home
                  </MobileNavItem>
                  <MobileNavItem to="/blog" onClick={closeMenu}>
                    Blog
                  </MobileNavItem>
                  <MobileNavItem to="/contact" onClick={closeMenu}>
                    Contact
                  </MobileNavItem>
                </div>
              </MobileMenu>
            </>
          )}
        </AnimatePresence>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 